import { BusinessPlanTemplate } from '../../../mocks/businessPlanTemplates';

interface Props {
  templates: BusinessPlanTemplate[];
  onClose: () => void;
  onUseTemplate: (template: BusinessPlanTemplate) => void;
}

const BADGE_STYLES: Record<string, string> = {
  Popular: 'bg-amber-100 text-amber-700 border border-amber-200',
  New: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
  Trending: 'bg-rose-100 text-rose-700 border border-rose-200',
};

const COMPARE_ROWS = [
  { label: 'Category', key: 'category', icon: 'ri-grid-line' },
  { label: 'Page Count', key: 'pageCount', icon: 'ri-pages-line' },
  { label: 'Sections', key: 'sections', icon: 'ri-list-check-2' },
  { label: 'Style Tags', key: 'tags', icon: 'ri-price-tag-3-line' },
  { label: 'Color Style', key: 'colorPalette', icon: 'ri-palette-line' },
  { label: 'Badge', key: 'badge', icon: 'ri-award-line' },
];

function renderCellValue(key: string, template: BusinessPlanTemplate) {
  const [primary, accent] = template.colorPalette;

  if (key === 'pageCount') {
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
          <div
            className="h-2 rounded-full transition-all"
            style={{ width: `${Math.min(100, (template.pageCount / 50) * 100)}%`, background: primary }}
          />
        </div>
        <span className="text-sm font-bold text-slate-800 whitespace-nowrap">{template.pageCount}p</span>
      </div>
    );
  }

  if (key === 'sections') {
    return (
      <div className="space-y-1">
        {template.sections.slice(0, 5).map(s => (
          <div key={s} className="flex items-center gap-1.5 text-xs text-slate-600">
            <i className="ri-check-line text-emerald-500 flex-shrink-0"></i>
            <span className="truncate">{s}</span>
          </div>
        ))}
        {template.sections.length > 5 && (
          <span className="text-xs text-slate-400 pl-4">+{template.sections.length - 5} more</span>
        )}
      </div>
    );
  }

  if (key === 'tags') {
    return (
      <div className="flex flex-wrap gap-1">
        {template.tags.map(tag => (
          <span key={tag} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full font-medium">{tag}</span>
        ))}
      </div>
    );
  }

  if (key === 'colorPalette') {
    return (
      <div className="flex items-center gap-2">
        {template.colorPalette.map((color, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <div className="w-6 h-6 rounded-md border border-slate-200 shadow-sm" style={{ background: color }} />
            <span className="text-xs text-slate-400 font-mono hidden xl:block">{color}</span>
          </div>
        ))}
      </div>
    );
  }

  if (key === 'badge') {
    return template.badge ? (
      <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${BADGE_STYLES[template.badge]}`}>
        {template.badge}
      </span>
    ) : (
      <span className="text-xs text-slate-300 italic">—</span>
    );
  }

  if (key === 'category') {
    return (
      <span className="px-2.5 py-1 rounded-lg text-xs font-semibold" style={{ background: `${primary}18`, color: primary }}>
        {template.category}
      </span>
    );
  }

  return <span className="text-sm text-slate-700">{String((template as Record<string, unknown>)[key] ?? '—')}</span>;
}

export default function CompareModal({ templates, onClose, onUseTemplate }: Props) {
  const colWidth = templates.length === 2 ? 'w-1/2' : 'w-1/3';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-violet-50 rounded-xl flex items-center justify-center">
              <i className="ri-layout-column-line text-violet-600 text-lg"></i>
            </div>
            <div>
              <h2 className="text-lg font-extrabold text-slate-900">Compare Templates</h2>
              <p className="text-xs text-slate-400">Side-by-side comparison of {templates.length} templates</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-xl border border-slate-200 text-slate-400 hover:bg-slate-50 hover:text-slate-700 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {/* Template header cards */}
          <div className="flex border-b border-slate-100 sticky top-0 bg-white z-10">
            <div className="w-36 flex-shrink-0 px-6 py-4 flex items-end">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Feature</span>
            </div>
            {templates.map(t => {
              const [primary, accent] = t.colorPalette;
              return (
                <div key={t.id} className={`${colWidth} flex-shrink-0 px-5 py-4 border-l border-slate-100`}>
                  {/* Mini visual */}
                  <div
                    className="h-20 rounded-xl mb-3 relative overflow-hidden flex items-end p-3"
                    style={{ background: `linear-gradient(135deg, ${primary}18, ${accent}28)` }}
                  >
                    <div className="flex items-end gap-0.5 w-full">
                      {[40, 65, 50, 80, 55, 90, 70].map((h, i) => (
                        <div
                          key={i}
                          className="flex-1 rounded-t"
                          style={{ height: `${h}%`, background: i % 2 === 0 ? primary : accent, opacity: 0.7 }}
                        />
                      ))}
                    </div>
                    {t.badge && (
                      <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-bold ${BADGE_STYLES[t.badge]}`}>
                        {t.badge}
                      </div>
                    )}
                  </div>
                  <h3 className="text-sm font-bold text-slate-900 leading-snug line-clamp-2 mb-1">{t.name}</h3>
                  <p className="text-xs text-slate-400 line-clamp-2 leading-relaxed">{t.description}</p>
                </div>
              );
            })}
          </div>

          {/* Comparison rows */}
          {COMPARE_ROWS.map((row, rowIdx) => (
            <div
              key={row.key}
              className={`flex border-b border-slate-50 ${rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}
            >
              {/* Label */}
              <div className="w-36 flex-shrink-0 px-6 py-4 flex items-start gap-2">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className={`${row.icon} text-slate-400 text-sm`}></i>
                </div>
                <span className="text-xs font-semibold text-slate-500 leading-snug">{row.label}</span>
              </div>
              {/* Values */}
              {templates.map(t => (
                <div key={t.id} className={`${colWidth} flex-shrink-0 px-5 py-4 border-l border-slate-100`}>
                  {renderCellValue(row.key, t)}
                </div>
              ))}
            </div>
          ))}

          {/* Sections full comparison */}
          <div className="flex border-b border-slate-100">
            <div className="w-36 flex-shrink-0 px-6 py-4 flex items-start gap-2">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                <i className="ri-file-list-3-line text-slate-400 text-sm"></i>
              </div>
              <span className="text-xs font-semibold text-slate-500">All Sections</span>
            </div>
            {templates.map(t => {
              const [primary] = t.colorPalette;
              return (
                <div key={t.id} className={`${colWidth} flex-shrink-0 px-5 py-4 border-l border-slate-100`}>
                  <div className="space-y-1.5">
                    {t.sections.map((s, i) => (
                      <div key={s} className="flex items-center gap-2">
                        <div
                          className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 text-xs font-bold"
                          style={{ background: `${primary}18`, color: primary }}
                        >
                          {i + 1}
                        </div>
                        <span className="text-xs text-slate-700">{s}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer CTAs */}
        <div className="flex border-t border-slate-100 flex-shrink-0 bg-white">
          <div className="w-36 flex-shrink-0 px-6 py-4 flex items-center">
            <button
              onClick={onClose}
              className="text-xs font-semibold text-slate-500 hover:text-slate-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              ← Back
            </button>
          </div>
          {templates.map(t => {
            const [primary, accent] = t.colorPalette;
            return (
              <div key={t.id} className={`${colWidth} flex-shrink-0 px-5 py-4 border-l border-slate-100`}>
                <button
                  onClick={() => onUseTemplate(t)}
                  className="w-full py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-md"
                  style={{ background: `linear-gradient(135deg, ${primary}, ${accent})` }}
                >
                  <i className="ri-edit-line"></i>
                  Use This Template
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
